export function useRestaurantItemList(title, description, data) {
  const baseUrl = useEnv("prodUrl");
  const router = useRouter();

  const itemListElements = data?.map((item, index) => {
    return {
      "@type": "ListItem",
      position: index + 1,
      item: {
        ...getCommonRestaurantData(item),
      },
    };
  });

  useJsonld({
    "@context": "https://schema.org",
    "@type": "ItemList",
    url: `${baseUrl}${router.currentRoute.value.path}`,
    name: title,
    description: description,
    itemListElement: itemListElements,
  });
}

export function useRestaurantItemStructuredData(data) {
  useJsonld({
    "@context": "https://schema.org",
    ...getCommonRestaurantData(data),
  });
}

export function useGuideItemList(heading, leading, data) {
  const baseUrl = useEnv("prodUrl");
  const router = useRouter();

  const params = router.currentRoute.value.params;

  const isArticle = params?.all?.[0] === "artiklar";
  const isGuidePage = params?.all?.[0] === "guider";

  if (!isArticle && !isGuidePage) {
    return;
  }

  const itemListElements = data?.map((item, index) => {
    return {
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": isGuidePage ? "Guide" : "Article",
        name: item.title,
        description: item.excerpt,
        url: `${baseUrl}${item.path}`,
      },
    };
  });

  useJsonld({
    "@context": "https://schema.org",
    "@type": "ItemList",
    url: `${baseUrl}${router.currentRoute.value.path}`,
    itemListElement: itemListElements,
    name: heading,
    description: leading,
  });
}

export function useGuideItemStructuredData(
  data,
  author,
  restaurantReviewBlocks = []
) {
  const baseUrl = useEnv("prodUrl");
  const router = useRouter();

  const params = router.currentRoute.value.params;

  const isArticle = params?.all?.[0] === "artiklar";
  const isGuidePage = params?.all?.[0] === "guider";

  const hasAuthor = Object.keys(author || {}).length > 0;

  if (!isArticle && !isGuidePage) {
    return;
  }

  const baseData = {
    "@context": "https://schema.org",
    "@type": isGuidePage ? "Guide" : "Article",
    name: data.title,
    description: data.excerpt,
    datePublished: isGuidePage ? data.modifiedDate : data.publishDate,
    url: `${baseUrl}${data.path}`,
    ...(hasAuthor && {
      author: {
        "@type": author.id === 3 ? "Organization" : "Person",
        name: author.name,
      },
    }),
  };

  if (!isGuidePage) {
    useJsonld(baseData);
    return;
  }

  const allRestaurants = restaurantReviewBlocks
    .map((block) => block?.data?.restaurants || [])
    .flat();

  let itemList = {};

  // If a guide has multiple blocks of restaurant_reviews, we concat it into a single list.
  if (restaurantReviewBlocks.length > 1) {
    itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: data.title,
      description: data.excerpt,
      itemListElement: allRestaurants.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          ...getCommonRestaurantData(item.restaurant, item.title, item.excerpt),
        },
      })),
    };
  } else {
    const block = restaurantReviewBlocks?.[0];
    itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: block?.data?.title,
      description: block?.data?.excerpt,
      itemListElement: block?.data?.restaurants?.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          ...getCommonRestaurantData(item.restaurant, item.title, item.excerpt),
        },
      })),
    };
  }

  const baseObject = {
    "@context": "https://schema.org",
    "@graph": [baseData, itemList],
  };

  useJsonld(baseObject);
}

const getCommonRestaurantData = (data, title = "", description = "") => {
  const router = useRouter();
  const baseUrl = useEnv("prodUrl");

  if (!data) {
    return {
      "@type": "Restaurant",
      name: title,
      description: description,
    };
  }

  const openingHours = data?.openingHours
    ?.map((item) => {
      if (item.times.length === 0) {
        return null;
      }

      const base = "https://schema.org/";

      const dayMap = {
        0: `${base}Monday`,
        1: `${base}Tuesday`,
        2: `${base}Wednesday`,
        3: `${base}Thursday`,
        4: `${base}Friday`,
        5: `${base}Saturday`,
        6: `${base}Sunday`,
      };

      const open = item.times[0].open;
      const close = item.times[0].close;

      return {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: dayMap[item.day],
        opens: open,
        closes: close,
      };
    })
    .filter(Boolean);

  const menu = Array.isArray(data?.menus)
    ? data.menus.find((menu) => !!menu.source?.url)?.source?.url
    : "";

  const url = router.resolve({
    name: "restaurants-slug",
    params: { slug: data.slug },
  })?.href;

  const image = !!data.cover ? useCloudinaryImage(data.cover, 600, 400) : null;

  const cuisine = getCuisine(data);

  const canBook = data.hash && data.bookable;
  const bookingUrl = canBook
    ? `${baseUrl}${url}?widget=${useCreateWidgetQuery(data.hash)}`
    : false;

  return {
    "@type": "Restaurant",
    name: title || data.name,
    description: description || data.shortDescription,
    telephone: data.phone ? data.phone.trim() : "",
    acceptsReservations: bookingUrl,
    email: data.email,
    url: `${baseUrl}${url}`,
    image: image,
    address: {
      "@type": "PostalAddress",
      addressLocality: data.city,
      streetAddress: data.address,
      // addressCountry: "SE", // Todo -- should have this.
      // postalCode: "", // Todo -- should have this.
    },
    servesCuisine: cuisine,
    hasMenu: menu,
    openingHoursSpecification: openingHours,
    geo: {
      "@type": "GeoCoordinates",
      latitude: data.location?.lat,
      longitude: data.location?.lon,
    },
  };
};

const getCuisine = (data) => {
  const tags = data?.tags || [];

  if (tags.length === 0) {
    return "";
  }

  const fromWp = Object.keys(tags[0]).includes("groupId");

  if (fromWp) {
    return tags
      .filter((tag) => tag.groupId === 2)
      .map((tag) => tag.label)
      .join(", ");
  }

  return (
    tags
      .find((tag) => tag.id === 2)
      ?.tags.map((tag) => tag.label)
      .join(", ") || ""
  );
};
